// Generated by GitHub Copilot
import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import { OFFERING_BY_SLUG, OFFERING_GROUPS } from '../../constants/offerings';
import styles from './OfferingDetail.module.css';

/**
 * Toggle between tab bar layouts:
 *   true  → new flat strip: all 4 offerings in one row, active tab is purple-filled
 *   false → legacy two-part bar: group tabs (left) + sub-tabs (right)
 */
const layout = true;

// Flat list of every offering across all groups, in definition order
const ALL_OFFERINGS = OFFERING_GROUPS.flatMap((g) => g.offerings);

/**
 * Icon map — returns a simple SVG icon per category string.
 * Keeps the component self-contained without an icon library dependency.
 *
 * @param {string} category - Category label from the offering data.
 */
const CategoryIcon = ({ category }) => {
  const cls = styles.categoryIcon;

  const icons = {
    'Conversation starter': (
      <svg className={cls} fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
    'Industry Origination': (
      <svg className={cls} fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    'Archetype Pack': (
      <svg className={cls} fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
      </svg>
    ),
    'Location Strategy': (
      <svg className={cls} fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
    'AI enabled GCC': (
      <svg className={cls} fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
      </svg>
    ),
    'GCC credentials': (
      <svg className={cls} fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
  };

  return icons[category] || (
    <svg className={cls} fill="none" stroke="currentColor" strokeWidth={1.6} viewBox="0 0 24 24" aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
};

/**
 * OfferingDetail page — displays full asset cards and "Why Accenture" section
 * for a selected GCC journey offering.
 *
 * URL: /offering/:slug
 */
const OfferingDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const match = OFFERING_BY_SLUG[slug];

  // Graceful fallback for unknown slugs
  if (!match) {
    return (
      <div className={styles.notFoundPage}>
        <Navbar />
        <div className={styles.navPadding} />
        <div className={styles.notFoundContent}>
          Offering not found.
        </div>
        <Footer />
      </div>
    );
  }

  const { offering, group } = match;

  return (
    <div className={styles.page}>
      <Navbar />
      <div className={styles.navPadding} />

<main key={slug} className={styles.main}>

        {/* ── Back button ── */}
        <button
          type="button"
          onClick={() => navigate('/')}
          className={styles.backButton}
          aria-label="Go back"
        >
          <svg className={styles.backIcon} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        {/* ── Tab bar ── */}
        {layout ? (
          /* New layout: all 4 offerings in a single flat strip */
          <div className={styles.flatTabStrip} role="tablist" aria-label="GCC Offering tabs">
            {ALL_OFFERINGS.map((o) => {
              const isActive = o.slug === offering.slug;
              return (
                <button
                  key={o.slug}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => navigate(`/offering/${o.slug}`)}
                  className={`${styles.flatTab} ${isActive ? styles.flatTabActive : styles.flatTabInactive}`}
                >
                  {o.label}
                </button>
              );
            })}
          </div>
        ) : (
          /* Legacy layout: group tabs (left) + offering sub-tabs (right) */
          <div className={styles.tabBar}>

            {/* Left — group tabs (No GCC / Existing GCC) */}
            <div className={styles.groupTabs} role="tablist" aria-label="GCC journey group">
              {OFFERING_GROUPS.map((g) => {
                const isActive = g.groupSlug === group.groupSlug;
                return (
                  <button
                    key={g.groupSlug}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => navigate(`/offering/${g.offerings[0].slug}`)}
                    className={`${styles.groupTabBase} ${isActive ? styles.groupTabActive : styles.groupTabInactive}`}
                  >
                    {g.groupLabel}
                  </button>
                );
              })}
            </div>

            {/* Right — offering sub-tabs (pill style) */}
            <div className={styles.offeringTabs} role="tablist" aria-label="Offering tabs">
              {group.offerings.map((o) => {
                const isActive = o.slug === offering.slug;
                return (
                  <button
                    key={o.slug}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => navigate(`/offering/${o.slug}`)}
                    className={`${styles.offeringTabBase} ${isActive ? styles.offeringTabActive : styles.offeringTabInactive}`}
                  >
                    {o.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Offering heading + description ── */}
        <h1 className={styles.offeringTitle}>{offering.title}</h1>
        <p className={styles.offeringDesc}>
          {offering.description}
        </p>

        {/* ── Assets and Materials ── */}
        <section aria-labelledby="assets-heading">
          <h2 id="assets-heading" className={styles.sectionHeading}>
            Assets and Materials
          </h2>
          <div className={styles.assetGrid}>
            {offering.assets.map((asset, index) => (
              <article
                key={`${asset.category}-${asset.title}-${index}`}
                className={styles.assetCard}
              >
                {/* Category label + icon */}
                <div className={styles.assetCategoryRow}>
                  <CategoryIcon category={asset.category} />
                  <span className={styles.assetCategory}>{asset.category}</span>
                </div>

                {/* Asset title */}
                <h3 className={styles.assetTitle}>{asset.title}</h3>

                {/* Description */}
                <p className={styles.assetDesc}>{asset.description}</p>

                {/* Link(s) */}
                {asset.links ? (
                  <div className={styles.assetMultiLinks}>
                     {asset.links.map((l, i) => (
                      <span key={l.label} className={styles.linkItem}>
                        {l.url ? (
                            l.url.startsWith('/') ? (
                            <Link to={l.url} className={styles.assetLink}>{l.label}</Link>
                          ) : (
                          <a
                            href={l.url}
                            target="_blank"
                              rel="noopener noreferrer"
                              className={styles.assetLink}
                              aria-label={`Open ${l.label} (opens in a new tab)`}
                            >
                              {l.label}
                            </a>
                           )
                        ) : (
                          <span className={styles.assetLinkDisabled}>{l.label}</span>
                        )}
                        {i < asset.links.length - 1 && (
                          <span className={styles.separator} aria-hidden="true"> | </span>
                        )}
                      </span>
                    ))}
                  </div>
                ) : asset.url ? (
                 asset.url.startsWith('/') ? (
                    <Link to={asset.url} className={styles.assetLink}>{asset.link}</Link>
                  ) : (
                    <a
                      href={asset.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.assetLink}
                      aria-label={`Open ${asset.link} (opens in a new tab)`}
                    >
                      {asset.link}
                    </a>    
                       )             
                ) : (
                  <span className={styles.assetLinkDisabled}>{asset.link}</span>
                )}
              </article>
            ))}
          </div>
        </section>

        {/* ── Why Accenture ── */}
        {/* <section aria-labelledby="why-accenture-heading">
          <h2 id="why-accenture-heading" className={styles.sectionHeading}>
            Why Accenture?
          </h2>
          <div className={styles.whyGrid}>
            {offering.whyAccenture.map((item) => (
              <div
                key={item.title}
                className={styles.whyCard}
              > */}
                {/* Lightning bolt icon */}
                {/* <div className={styles.whyIconWrapper}>
                  <svg className={styles.whyIcon} fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <p className={styles.whyText}>
                  <span className={styles.whyTextBold}>{item.title} — </span>
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </section> */}

      </main>

      <Footer />
    </div>
  );
};

export default OfferingDetail;
